import { useEffect } from 'react';
import {
  FieldErrors,
  FieldValues,
  useForm,
  UseFormProps,
  useWatch,
} from 'react-hook-form';

import { useXemploFormStore } from './xemplo-form-store';
import { SubmitResult } from './xemplo-form-store.types';

/**
 *
 * @param formId Unique identifier for the form
 * @param formOptions RHF form options (eg, defaultValues, mode, etc.)
 * @returns RHF form methods with enhanced functionality (syncing to store, submit handler)
 */

export function useXemploForm<TValues extends FieldValues>(
  formId: string,
  formOptions?: UseFormProps<TValues>
) {
  const methods = useForm<TValues>(formOptions);
  const values = useWatch({ control: methods.control });
  const { setFormValues, setFormSubmit } = useXemploFormStore();

  // Sync form values to store
  useEffect(() => {
    if (values) {
      setFormValues(formId, values);
    }
  }, [values, formId, setFormValues]);

  // Sync submit handler to zustand store
  useEffect(() => {
    const submitFn = () =>
      new Promise<SubmitResult<TValues>>((resolve) => {
        const onValid = (data: TValues) => {
          resolve({ success: true, data });
        };

        const onInvalid = (errors: FieldErrors<TValues>) => {
          resolve({ success: false, errors });
        };

        methods.handleSubmit(onValid, onInvalid)();
      });

    setFormSubmit(formId, submitFn);
  }, [formId, methods, setFormSubmit]);

  return methods;
}
